/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useMemo } from 'react';
import { Link } from '@/components/link';
// import { LogIn } from 'lucide-react';
import { useAuth } from '@/providers/auth';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { UserMenu } from './user-menu';
import { UserIcon } from '@/components/icons';
import { cn } from '@/lib/utils';

export default function UserActions({ className }: { className?: string }): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    const isAuthenticated: boolean = useMemo(() => {
        // Check if user is authenticated (has valid token and is registered)
        return Boolean(session?.userType === 'registered' && session?.customerId);
    }, [session]);

    const accountLink = isAuthenticated ? '/account/overview' : '/login';
    const ariaLabel = isAuthenticated ? tAccount('myAccount') : t('signIn');
    // const icon = isAuthenticated ? <UserIcon className="size-5" /> : <LogIn className="size-5" />;

    const trigger = (
        <Button
            variant="ghost"
            className={cn(
                'cursor-pointer hover:bg-transparent hover:opacity-50 transition-opacity p-0 size-9 min-w-9 min-h-9 flex items-center justify-center rounded-none',
                className
            )}
            asChild>
            <Link to={accountLink} aria-label={ariaLabel} className="size-9 flex items-center justify-center p-0">
                <UserIcon className="size-9 p-0" />
            </Link>
        </Button>
    );

    return <UserMenu isAuthenticated={isAuthenticated} trigger={trigger} />;
}
